import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Upload, 
  FileText, 
  AlertTriangle, 
  Loader2, 
  Info, 
  FilesIcon,
  X,
  Mail,
  ExternalLink
} from "lucide-react";
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { uploadFile } from '@/lib/fileHandler';
import { createInsuranceComparison } from '@/lib/comparisonHandler';
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import SendEmailForm from '../email-preview/SendEmailForm';

interface ChatInsuranceComparisonFormProps {
  onAnalysisComplete: (content: string) => void;
  onClose: () => void;
  onLinkGenerated?: (linkData: {url: string, id: string, type: string, timestamp: string}) => void;
}

const ChatInsuranceComparisonForm = ({ onAnalysisComplete, onClose, onLinkGenerated }: ChatInsuranceComparisonFormProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [apiError, setApiError] = useState<string | null>(null);
  const [includeDocumentLinks, setIncludeDocumentLinks] = useState(true);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const { user, credits, refreshProfile } = useAuth();
  const { toast } = useToast();
  
  const steps = [
    { id: 1, title: 'Dokumente hochladen' },
    { id: 2, title: 'Optionen konfigurieren' },
    { id: 3, title: 'Übersicht & Bestätigung' }
  ];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    const selectedFile = event.target.files[0];
    
    if (selectedFile.type === 'application/pdf') {
      setFiles(prevFiles => [...prevFiles, selectedFile]);
      setApiError(null);
    } else {
      toast({
        title: "Ungültiges Dateiformat",
        description: "Bitte laden Sie nur PDF-Dateien hoch.",
        variant: "destructive"
      });
    }
    
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCompare = async () => {
    if (!user) {
      toast({
        title: "Nicht angemeldet",
        description: "Bitte melden Sie sich an, um diese Funktion zu nutzen.",
        variant: "destructive"
      });
      return;
    }

    if (files.length === 0) {
      toast({
        title: "Keine Dateien",
        description: "Bitte laden Sie mindestens eine PDF-Datei hoch.",
        variant: "destructive"
      });
      return;
    }

    if (credits < 1) {
      toast({
        title: "Nicht genügend Credits",
        description: `Für die Analyse benötigen Sie 1 Credit. Sie haben derzeit ${credits} Credits.`,
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setApiError(null);
    setGeneratedContent(null);
    setSharingId(null);

    try {
      setUploadProgress(10);
      
      const uploadedFiles = [];
      const totalFiles = files.length;
      
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        const uploadedFile = await uploadFile(file, user.id);
        
        if (uploadedFile) {
          uploadedFiles.push(uploadedFile);
          setUploadProgress(10 + Math.floor((i + 1) / totalFiles * 30)); // Progress from 10% to 40%
        }
      }
      
      if (uploadedFiles.length === 0) {
        throw new Error('Keine Datei konnte hochgeladen werden.');
      }
      
      setUploadProgress(50);
      
      const { data, error } = await supabase.functions.invoke('generate-comparison-page', {
        body: {
          fileIds: uploadedFiles.map(file => file.id),
          companyInfo: localStorage.getItem('companyInfo') ? JSON.parse(localStorage.getItem('companyInfo')!) : null,
          includeDocumentLinks,
          includeSignature,
          additionalComments: comment
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (!data || !data.content) {
        throw new Error('Keine Inhalte von der API zurückgegeben');
      }
      
      setUploadProgress(80);
      
      const { data: emailData, error: emailError } = await supabase.functions.invoke('generate-content', {
        body: {
          prompt: `Erstelle eine kurze E-Mail-Benachrichtigung über einen neuen Versicherungsvergleich mit folgenden Details:
          
          ${comment ? `Beraterhinweise: ${comment}` : ''}
          
          Die E-Mail soll den Kunden informieren, dass ein neuer Versicherungsvergleich für ihn bereitsteht.
          Füge einen deutlich sichtbaren Button mit dem Text "Vergleich ansehen" ein.
          Der Button soll auf folgende URL verlinken: ${window.location.origin}/comparison/${data.sharingId}
          Der Vergleich enthält ${uploadedFiles.length} Versicherungsangebote.`,
          
          companyInfo: localStorage.getItem('companyInfo') ? JSON.parse(localStorage.getItem('companyInfo')!) : null,
          format: 'html',
          emailType: 'comparison_notification',
          includeDocumentLinks,
          fileDetails: uploadedFiles.map(file => ({
            name: file.filename,
            url: file.url,
            id: file.id
          }))
        }
      });
      
      if (emailError) {
        throw new Error(emailError.message);
      }
      
      if (!emailData || !emailData.content) {
        throw new Error('Keine E-Mail-Inhalte von der API zurückgegeben');
      }
      
      setUploadProgress(100);
      setGeneratedContent(emailData.content);
      setSharingId(data.sharingId);
      
      onAnalysisComplete(emailData.content);

      if (onLinkGenerated) {
        onLinkGenerated({
          url: `${window.location.origin}/comparison/${data.sharingId}`,
          id: data.sharingId,
          type: 'comparison',
          timestamp: new Date().toISOString()
        });
      }
      
      toast({
        title: "Vergleich erstellt",
        description: "Der Versicherungsvergleich wurde erfolgreich erstellt.",
      });
      
      refreshProfile();
      
    } catch (error: any) {
      console.error('Error in comparison creation:', error);
      setApiError(error.message || 'Es gab einen Fehler bei der Verarbeitung. Bitte versuchen Sie es später erneut.');
      
      toast({
        title: "Fehler bei der Erstellung",
        description: error.message || "Es gab einen Fehler bei der Verarbeitung. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmail = () => {
    setShowEmailForm(true);
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Versicherungsvergleich</h3>
        <Button 
          variant="ghost" 
          size="sm"
          className="text-gray-400 hover:text-white p-1 h-auto"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex justify-between mb-4">
        {steps.map((step) => (
          <div 
            key={step.id} 
            className={`flex flex-col items-center ${currentStep === step.id ? 'text-jade' : 'text-gray-500'}`}
          >
            <div 
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mb-1
                ${currentStep === step.id ? 'bg-jade text-white' : 
                  currentStep > step.id ? 'bg-jade/30 text-white' : 'bg-gray-800 text-gray-400'}`}
            >
              {step.id}
            </div>
            <span className="text-xs hidden sm:block">{step.title}</span>
          </div>
        ))}
      </div>

      {apiError && (
        <div className="p-3 bg-red-900/20 rounded-lg border border-red-800/40 text-white">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div>{apiError}</div>
          </div>
        </div>
      )}

      {generatedContent && (
        <div className="space-y-4">
          <div className="p-3 bg-green-900/20 rounded-lg border border-green-800/40 text-white">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div>Vergleich wurde erfolgreich erstellt!</div>
            </div>
          </div>
          
          {sharingId && (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm text-gray-300">Öffentlicher Link zum Vergleich:</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs border-gray-700 bg-gray-800"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/comparison/${sharingId}`);
                      toast({
                        title: "Link kopiert",
                        description: "Der Link wurde in die Zwischenablage kopiert.",
                      });
                    }}
                  >
                    Link kopieren
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-gray-700 bg-gray-800"
                    onClick={() => window.open(`${window.location.origin}/comparison/${sharingId}`, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Öffnen
                  </Button>
                </div>
              </div>
              <div className="text-xs text-gray-400 bg-gray-800 p-2 rounded border border-gray-700 break-all">
                {`${window.location.origin}/comparison/${sharingId}`}
              </div>
            </div>
          )}
          
          {!showEmailForm ? (
            <Button
              type="button"
              className="w-full bg-jade hover:bg-jade-light text-white"
              onClick={handleSendEmail}
            >
              <Mail className="h-4 w-4 mr-2" />
              E-Mail an Kunden senden
            </Button>
          ) : (
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <SendEmailForm 
                emailContent={generatedContent}
                userEmail={user?.email}
              />
            </div>
          )}
          
          <Button
            type="button"
            variant="ghost"
            className="w-full border border-gray-700 hover:bg-gray-800 mt-2"
            onClick={onClose}
          >
            Zurück zum Chat
          </Button>
        </div>
      )}

      {!generatedContent && currentStep === 1 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="insurance-files-upload" className="text-white">
              Versicherungsdokumente hochladen (PDF)
            </Label>
            
            <div 
              className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-jade transition-colors cursor-pointer"
              onClick={() => document.getElementById('insurance-files-upload')?.click()}
            >
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-300 mb-1">Dateien hierher ziehen oder klicken zum Hochladen</p>
              <p className="text-xs text-gray-500">Nur PDF-Dateien</p>
              <Input
                id="insurance-files-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            
            {files.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-300 mb-2">Hochgeladene Dateien:</p>
                {files.map((file, index) => (
                  <div key={index} className="p-3 bg-gray-800/60 rounded flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 w-5/6">
                      <FileText className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <span className="text-sm text-gray-200 truncate">{file.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeFile(index)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!generatedContent && currentStep === 2 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-white">
              Beraterhinweise (optional)
            </Label>
            <Textarea
              id="comment"
              placeholder="Fügen Sie spezifische Anweisungen hinzu..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="h-24 bg-gray-800 border-gray-700 text-white resize-none"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-800/40 rounded-lg">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-jade" />
              <div>
                <p className="text-sm font-medium text-white">Wissensdatenbank einbeziehen</p>
                <p className="text-xs text-gray-400">Relevante Informationen aus der Wissensdatenbank werden berücksichtigt</p>
              </div>
            </div>
            <Switch
              checked={includeKnowledgeBase}
              onCheckedChange={setIncludeKnowledgeBase}
              className="data-[state=checked]:bg-jade"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-800/40 rounded-lg">
            <div className="flex items-center gap-2">
              <FilesIcon className="h-5 w-5 text-jade" />
              <div>
                <p className="text-sm font-medium text-white">Dokument-Links einbeziehen</p>
                <p className="text-xs text-gray-400">Links zu den hochgeladenen Dokumenten werden im Vergleich angezeigt</p>
              </div>
            </div>
            <Switch
              checked={includeDocumentLinks}
              onCheckedChange={setIncludeDocumentLinks}
              className="data-[state=checked]:bg-jade"
            />
          </div>
        </div>
      )}

      {!generatedContent && currentStep === 3 && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-800/40 rounded-lg border border-gray-700">
            <h3 className="font-medium text-white mb-2">Zusammenfassung</h3>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex justify-between">
                <span>Anzahl Dokumente:</span>
                <span className="font-medium text-white">{files.length}</span>
              </li>
              <li className="flex justify-between">
                <span>Dokument-Links:</span>
                <span className="font-medium text-white">{includeDocumentLinks ? 'Ja' : 'Nein'}</span>
              </li>
              <li className="flex justify-between">
                <span>Wissensdatenbank:</span>
                <span className="font-medium text-white">{includeKnowledgeBase ? 'Einbezogen' : 'Nicht einbezogen'}</span>
              </li>
              <li className="flex justify-between">
                <span>Benötigte Credits:</span>
                <span className="font-medium text-jade">1 Credit</span>
              </li>
            </ul>
          </div>

          <div className="p-3 bg-amber-900/20 border border-amber-800/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                Die Analyse kann je nach Größe und Anzahl der Dokumente bis zu einer Minute dauern.
              </p>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-jade rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-400 text-center">
            {uploadProgress < 40 ? "Dokumente werden hochgeladen..." : 
             uploadProgress < 60 ? "Dokumente werden analysiert..." : 
             uploadProgress < 80 ? "Vergleich wird erstellt..." : "E-Mail wird generiert..."}
          </p>
        </div>
      )}

      {!generatedContent && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-400 flex items-center gap-1.5">
            <Badge variant="outline" className="bg-jade/20 text-jade border-jade/30">
              Credits: {credits}
            </Badge>
          </div>
          
          <div className="flex gap-2">
            {currentStep > 1 && !isLoading && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="border-gray-700 text-gray-200 hover:bg-gray-800"
                disabled={isLoading}
              >
                Zurück
              </Button>
            )}
            
            {currentStep < steps.length && !isLoading && (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-jade hover:bg-jade-light text-white"
                disabled={isLoading || (currentStep === 1 && files.length === 0)}
              >
                Weiter
              </Button>
            )}
            
            {currentStep === steps.length && !isLoading && (
              <Button
                type="button"
                onClick={handleCompare}
                className="bg-jade hover:bg-jade-light text-white"
                disabled={isLoading || files.length === 0}
              >
                Vergleich erstellen
              </Button>
            )}
            
            {isLoading && (
              <Button
                type="button"
                className="bg-jade hover:bg-jade-light text-white"
                disabled={true}
              >
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verarbeitung...
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInsuranceComparisonForm;
